import type React from "react";
import type {
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps,
} from "react-aria-components";
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  OverlayArrow,
  composeRenderProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

interface TooltipProps extends Omit<AriaTooltipProps, "children"> {
  variant?: "default" | "popover";
  children: React.ReactNode;
}

const tooltipStyles = tv({
  variants: {
    variant: {
      default:
        "group bg-dark-900 border border-dark-600 text-white text-sm rounded drop-shadow-lg px-3 py-1",
      popover:
        "bg-white shadow-lg border p-1 rounded-[.5rem] min-w-(--trigger-width)]",
    },
    isEntering: {
      true: "animate-in fade-in placement-bottom:slide-in-from-top-0.5 placement-top:slide-in-from-bottom-0.5 placement-left:slide-in-from-right-0.5 placement-right:slide-in-from-left-0.5 ease-out duration-200",
    },
    isExiting: {
      true: "animate-out fade-out placement-bottom:slide-out-to-top-0.5 placement-top:slide-out-to-bottom-0.5 placement-left:slide-out-to-right-0.5 placement-right:slide-out-to-left-0.5 ease-in duration-150",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export function Tooltip({ children, variant, ...props }: TooltipProps) {
  return (
    <AriaTooltip
      {...props}
      offset={10}
      className={composeRenderProps(props.className, (className, renderProps) =>
        tooltipStyles({ ...renderProps, variant, className })
      )}
    >
      {variant == "default" ? (
        <OverlayArrow>
          <svg
            width={8}
            height={8}
            viewBox="0 0 8 8"
            className="group-placement-bottom:rotate-180 group-placement-left:-rotate-90 group-placement-right:rotate-90 fill-dark-900"
          >
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
      ) : null}
      {children}
    </AriaTooltip>
  );
}

export function TooltipTrigger({
  children,
  delay = 100,
  closeDelay = 100,
  ...props
}: TooltipTriggerComponentProps) {
  return (
    <AriaTooltipTrigger {...props} delay={delay} closeDelay={closeDelay}>
      {children}
    </AriaTooltipTrigger>
  );
}
